import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import { isEqual, pick } from 'lodash';

import ActiveFilters from './active_filters';
import ClearFiltersButton from './clear_filters_button';
import MultipleButtonFilter from './multiple_button_filter';
import MultipleSelectFilter from './multiple_select_filter';
import SingleButtonFilter from './single_button_filter';
import { FilterConfig } from './types';

interface Props {
  allowClear?: boolean;
  collapseBy?: number;
  filters: { [key: string]: string };
  filtersConfig: FilterConfig[];
  focused?: boolean;
  insertClearAfter?: string;
  onChange: (updatedFilters: { [key: string]: string }) => void;
  onShowCollapsed?: () => void;
}

const AllFilters: React.FC<Props> = (props) => {
  const allowClear = () => {
    if (!props.allowClear) return false;

    const knownKeys = props.filtersConfig.map((c) => c.key);
    return !isEqual(defaultFilters(), pick(props.filters, knownKeys));
  };

  const clearFilters = () => {
    props.onChange({ ...props.filters, ...defaultFilters() });
  };

  const collapseByPerCollapsible = () => {
    if (!props.collapseBy) return 0;

    const collapsibleFilterCount = props.filtersConfig.filter((config) => config.collapsible).length;
    if (!collapsibleFilterCount) return 0;

    return props.collapseBy / collapsibleFilterCount;
  };

  const defaultFilters = () => {
    let defaultFilters = {};

    props.filtersConfig.map((filterConfig) => {
      defaultFilters[filterConfig.key] = filterConfig.default;
    });

    return defaultFilters;
  };

  const handleFilterChanged = (name: string, value: string) => {
    props.onChange({ ...props.filters, [name]: value });
  };

  const filtersConfigForLeft = () => {
    return props.filtersConfig.filter((filterConfig) => !filterConfig.right);
  };

  const filtersConfigForRight = () => {
    return props.filtersConfig.filter((filterConfig) => filterConfig.right);
  };

  const renderFiltersConfig = (filtersConfig) => {
    return (
      <>
        {
          filtersConfig.map((filterConfig, idx) => {
            if (filterConfig.multiple) {
              return (
                <>
                  <div key={idx} className={cx(filterConfig.className, 'd-flex')} style={{ gap: 10 }}>
                    {
                      filterConfig.multipleStyle === 'button' ? (
                        <MultipleButtonFilter
                          collapseBy={filterConfig.collapsible ? collapseByPerCollapsible() : 0}
                          disabled={props.focused && !filterConfig.allowWhileFocused}
                          filterConfig={filterConfig}
                          onChange={handleFilterChanged}
                          onShowCollapsed={props.onShowCollapsed}
                          value={props.filters[filterConfig.key]}
                        />
                      ) : (
                        <MultipleSelectFilter
                          disabled={props.focused && !filterConfig.allowWhileFocused}
                          filterConfig={filterConfig}
                          onChange={handleFilterChanged}
                          value={props.filters[filterConfig.key]}
                        />
                      )
                    }

                  </div>

                  {
                    shouldRenderClearButtonAfterFilter(filterConfig, idx) && (
                      <ClearFiltersButton clear={clearFilters} />
                    )
                  }
                </>
              );
            } else {
              return (
                 <>
                   <div key={idx} className={cx(filterConfig.className, 'd-flex')} style={{ gap: 10 }}>
                    <SingleButtonFilter
                      disabled={props.focused && !filterConfig.allowWhileFocused}
                      filterConfig={filterConfig}
                      onChange={handleFilterChanged}
                      value={props.filters[filterConfig.key]}
                    />
                  </div>

                  {
                    shouldRenderClearButtonAfterFilter(filterConfig, idx) && (
                      <ClearFiltersButton clear={clearFilters} />
                    )
                  }
                </>
              );
            }
          })
        }
      </>
    );
  };

  const shouldRenderClearButtonAfterFilter = (filterConfig, idx) => {
    if (!allowClear()) return false;

    if (props.insertClearAfter === filterConfig.key) {
      return true;
    } else if (!props.insertClearAfter && idx === props.filtersConfig.length - 1) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <div className="d-flex flex-1 gap-l">
      <div className="align-items-start d-flex gap-s flex-1 flex-wrap">
        {renderFiltersConfig(filtersConfigForLeft())}
      </div>

      {
        filtersConfigForRight().length > 0 && (
          <div className="d-flex flex-none">
            <div>{renderFiltersConfig(filtersConfigForRight())}</div>
          </div>
        )
      }
    </div>
  );
};

export default AllFilters;
